import React, { PropsWithChildren, useEffect, useState } from 'react';
import { BoloPanelFormType } from '@mark43/cad-api';
import { IntegratedFormProvider } from '../../../forms/integrated-form-provider';
import { RemovedEventPanelFormFieldsProvider } from '../../../event-panel/event-panel-form/form-persistence/use-locally-removed-fields';
import { useForm } from 'react-hook-form';
import { getBoloPanelFormStubValues } from '../form/bolo-panel-form-stub-values';
import { useBoloApi } from './use-bolo-api';
import { useBoloPanelFormValidationResolver } from '../form/use-bolo-panel-validation-resolver';
import { useRefreshBoloPanel } from '../form/use-refresh-bolo-data';
import { useBoloQuery } from './use-bolo-query';
import { BoloPanelMetadataProvider } from './bolo-panel-metadata-context';
import { BoloPanelMetadataStore } from './bolo-panel-metadata-store';
import { useIntegratedStreams } from '../../../common/hooks/use-integrated-streams';
import { BoloPanelAdditionalInfosProvider } from '../additional-infos/bolo-panel-additional-infos-provider';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import { useTrackHistoricalBoloPanels } from './use-track-historical-bolo-panels';
import { BoloManagerRegister } from './bolo-manager-register';
import { WorkspacePanel } from '../../../panels/workspace-panel';

interface IntegratedBoloPanelProviderProps {
    children: React.ReactNode;
    boloId?: number;
    workspacePanel?: WorkspacePanel<'BOLO'>;
}

/**
 * Warning: Do not put anything that utilizes useWorkspacePanel in here. First responder uses this hook and
 * does not have access to the workspace panel.
 */
export const IntegratedBoloPanelProvider = ({
    children,
    boloId,
    workspacePanel,
}: IntegratedBoloPanelProviderProps) => {
    const resolver = useBoloPanelFormValidationResolver();

    const boloPanelQuery = useBoloQuery({
        boloId,
        queryKey: workspacePanel?.uuid,
    });

    const form = useForm<BoloPanelFormType>({
        defaultValues: getBoloPanelFormStubValues(boloId),
        mode: 'onBlur',
        resolver,
    });

    const boloMetadataStore = useState(new BoloPanelMetadataStore())[0];
    const refreshBoloPanel = useRefreshBoloPanel({
        boloForm: form,
        boloMetadataStore,
        workspacePanel,
    });

    const { saveBoloMutation, onSubmit } = useBoloApi({
        onSaveBolo: (boloPanelView) => refreshBoloPanel(boloPanelView),
    });

    useIntegratedStreams({
        query: boloPanelQuery,
        streamNames: ['boloPanelView'],
        onMessage: (data) => {
            const boloView = data.payload;

            if (boloView.boloPanelForm.id === boloId) {
                refreshBoloPanel(boloView);
            }
        },
        enabled: !!boloId,
        onReconnect: () => boloPanelQuery.refetch(),
    });

    const maybeBoloPanelView = boloPanelQuery?.data?.boloPanelView;

    useEffect(
        function setBoloPanelData() {
            if (maybeBoloPanelView) {
                refreshBoloPanel(maybeBoloPanelView);
            }
        },
        [maybeBoloPanelView, refreshBoloPanel]
    );

    return (
        <RemovedEventPanelFormFieldsProvider>
            <IntegratedFormProvider<BoloPanelFormType>
                form={form}
                mutation={saveBoloMutation}
                onSubmit={onSubmit}
                inputSize="sm"
                query={boloPanelQuery}
                withEditable
            >
                <BoloPanelMetadataProvider value={boloMetadataStore}>
                    <BoloPanelAdditionalInfosProvider>{children}</BoloPanelAdditionalInfosProvider>
                </BoloPanelMetadataProvider>
            </IntegratedFormProvider>
        </RemovedEventPanelFormFieldsProvider>
    );
};

export const WorkspacePanelIntegratedBoloPanelProvider = ({
    children,
    boloId,
}: PropsWithChildren<{ boloId?: number }>) => {
    const workspacePanel = useWorkspacePanel<'BOLO'>();
    useTrackHistoricalBoloPanels();

    return (
        <IntegratedBoloPanelProvider workspacePanel={workspacePanel} boloId={boloId}>
            <BoloManagerRegister />
            {children}
        </IntegratedBoloPanelProvider>
    );
};
